<template>
  <div class="p-0">
    <header class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Calendario de ausencias</h1>
        <p class="mt-1 text-sm text-gray-600">Vista de licencias y franquicias aprobadas y en tramite.</p>
      </div>
      <router-link to="/leave/requests/new?as=manager" class="primary-button">
        <i class="fa-solid fa-plus mr-2"></i>
        Nueva solicitud
      </router-link>
    </header>

    <nav class="module-tabs">
      <router-link to="/leave/inbox" class="module-tab">Bandeja</router-link>
      <router-link to="/leave/requests" class="module-tab">Mis solicitudes</router-link>
      <router-link to="/leave/calendar" class="module-tab module-tab-active">Calendario</router-link>
      <router-link to="/leave/admin/articles" class="module-tab">Administracion</router-link>
    </nav>

    <section class="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-5">
      <div class="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
        <div class="flex items-center gap-2">
          <button type="button" @click="changeMonth(-1)" class="icon-button">
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          <div class="min-w-44 text-center font-bold text-gray-900 capitalize">{{ monthTitle }}</div>
          <button type="button" @click="changeMonth(1)" class="icon-button">
            <i class="fa-solid fa-chevron-right"></i>
          </button>
          <button type="button" @click="goToday" class="secondary-button">Hoy</button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 flex-1">
          <select v-model="filters.user_id" @change="fetchRequests" class="field">
            <option value="">Todos los docentes</option>
            <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
              {{ teacherFullName(teacher) }}
            </option>
          </select>
          <select v-model="filters.leave_article_id" @change="fetchRequests" class="field">
            <option value="">Todos los articulos</option>
            <option v-for="article in articles" :key="article.id" :value="article.id">
              {{ articleTitle(article) }}
            </option>
          </select>
          <button type="button" @click="fetchRequests" class="secondary-button justify-center">
            Actualizar
          </button>
        </div>
      </div>

      <div class="mt-4 flex flex-wrap gap-3">
        <label v-for="status in visibleStatuses" :key="status.value" class="inline-flex items-center gap-2 text-sm text-gray-700">
          <input v-model="selectedStatuses" type="checkbox" :value="status.value" />
          <span class="w-3 h-3 rounded-full" :class="eventDotClass(status.value)"></span>
          {{ status.label }}
        </label>
      </div>
    </section>

    <div v-if="loading" class="py-10">
      <Loading />
    </div>
    <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
      {{ error }}
      <button type="button" @click="fetchRequests" class="ml-2 underline font-semibold">Reintentar</button>
    </div>
    <section v-else class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div class="hidden md:grid grid-cols-7 bg-gray-50 border-b border-gray-200">
        <div v-for="day in weekDays" :key="day" class="px-3 py-2 text-xs font-bold uppercase tracking-wide text-gray-500 text-center">
          {{ day }}
        </div>
      </div>

      <div class="hidden md:grid grid-cols-7">
        <div
          v-for="day in calendarDays"
          :key="day.iso"
          class="min-h-32 border-b border-r border-gray-100 p-2"
          :class="day.inMonth ? 'bg-white' : 'bg-gray-50 text-gray-400'"
        >
          <div class="flex items-center justify-between">
            <span class="text-sm font-semibold" :class="isToday(day.iso) ? 'bg-indigo-700 text-white rounded-full w-7 h-7 flex items-center justify-center' : ''">
              {{ day.dayNumber }}
            </span>
          </div>
          <div class="mt-2 space-y-1">
            <button
              v-for="event in eventsByDay(day.iso).slice(0, 2)"
              :key="event.id || event.request_id"
              type="button"
              @click="openEvent(event)"
              class="w-full text-left rounded px-2 py-1 text-xs font-semibold border-l-4 truncate"
              :class="eventClass(event.status)"
            >
              {{ shortTeacher(event) }} · {{ event.article_code || event.leave_article_code || 'ART' }}
            </button>
            <button
              v-if="eventsByDay(day.iso).length > 2"
              type="button"
              @click="openDay(day.iso)"
              class="w-full text-left text-xs font-semibold text-indigo-700 hover:text-indigo-900"
            >
              +{{ eventsByDay(day.iso).length - 2 }} mas
            </button>
          </div>
        </div>
      </div>

      <div class="md:hidden divide-y divide-gray-100">
        <article v-for="day in mobileDaysWithEvents" :key="day.iso" class="p-4">
          <h2 class="font-semibold text-gray-900">{{ formatDate(day.iso) }}</h2>
          <div class="mt-3 space-y-2">
            <button
              v-for="event in eventsByDay(day.iso)"
              :key="event.id || event.request_id"
              type="button"
              @click="openEvent(event)"
              class="w-full text-left rounded-lg px-3 py-2 text-sm font-semibold border-l-4"
              :class="eventClass(event.status)"
            >
              {{ requestTeacherName(event) }} · {{ requestTitle(event) }}
            </button>
          </div>
        </article>
        <div v-if="mobileDaysWithEvents.length === 0" class="p-8 text-center text-sm text-gray-500">
          No hay ausencias en este periodo.
        </div>
      </div>
    </section>

    <div v-if="dayModal.iso" class="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4">
      <div class="bg-white rounded-xl shadow-xl max-w-lg w-full overflow-hidden">
        <div class="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 class="text-lg font-bold text-gray-900">Ausencias del {{ formatDate(dayModal.iso) }}</h2>
          <button type="button" @click="dayModal.iso = ''" class="text-gray-400 hover:text-gray-700">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="p-5 space-y-3">
          <button
            v-for="event in eventsByDay(dayModal.iso)"
            :key="event.id || event.request_id"
            type="button"
            @click="openEvent(event)"
            class="w-full text-left border border-gray-200 rounded-lg p-3 hover:border-indigo-200"
          >
            <div class="font-semibold text-gray-900">{{ requestTeacherName(event) }}</div>
            <div class="text-sm text-gray-600">{{ requestTitle(event) }}</div>
            <span class="mt-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full border" :class="statusClass(event.status)">
              {{ statusLabel(event.status) }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/commons/Loading.vue';
import { leaveArticlesApi, leaveRequestsApi } from '@/service/leave.service';
import { teachersApi } from '@/service/user.service';
import {
  LEAVE_STATUSES,
  articleTitle,
  formatDate,
  formatMonthYear,
  requestTeacherName,
  requestTitle,
  statusClass,
  statusLabel,
  teacherFullName,
  unwrapList,
} from '@/utils/leave';

const toISO = (date) => date.toISOString().slice(0, 10);

export default {
  name: 'LeaveCalendarView',
  components: { Loading },
  data() {
    return {
      loading: false,
      error: '',
      monthDate: new Date(),
      requests: [],
      teachers: [],
      articles: [],
      filters: {
        user_id: '',
        leave_article_id: '',
      },
      selectedStatuses: ['approved', 'pending', 'observed', 'suspended'],
      dayModal: {
        iso: '',
      },
      weekDays: ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'],
    };
  },
  computed: {
    visibleStatuses() {
      return LEAVE_STATUSES.filter((status) => status.value);
    },
    monthTitle() {
      return formatMonthYear(toISO(new Date(this.monthDate.getFullYear(), this.monthDate.getMonth(), 1)));
    },
    monthRange() {
      const year = this.monthDate.getFullYear();
      const month = this.monthDate.getMonth();
      return {
        from: toISO(new Date(year, month, 1)),
        to: toISO(new Date(year, month + 1, 0)),
      };
    },
    calendarDays() {
      const year = this.monthDate.getFullYear();
      const month = this.monthDate.getMonth();
      const first = new Date(year, month, 1);
      const start = new Date(first);
      const firstWeekday = (first.getDay() + 6) % 7;
      start.setDate(first.getDate() - firstWeekday);
      return Array.from({ length: 42 }, (_, index) => {
        const date = new Date(start);
        date.setDate(start.getDate() + index);
        return {
          iso: toISO(date),
          dayNumber: date.getDate(),
          inMonth: date.getMonth() === month,
        };
      });
    },
    expandedEvents() {
      const events = [];
      this.requests
        .filter((request) => this.selectedStatuses.includes(request.status))
        .forEach((request) => {
          const start = new Date(`${request.start_date}T00:00:00`);
          const end = new Date(`${request.end_date || request.start_date}T00:00:00`);
          if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
          const cursor = new Date(start);
          while (cursor <= end) {
            const iso = toISO(cursor);
            if (iso >= this.monthRange.from && iso <= this.monthRange.to) {
              events.push({ ...request, dayIso: iso });
            }
            cursor.setDate(cursor.getDate() + 1);
          }
        });
      return events;
    },
    mobileDaysWithEvents() {
      return this.calendarDays.filter((day) => day.inMonth && this.eventsByDay(day.iso).length > 0);
    },
  },
  async created() {
    await Promise.all([this.fetchArticles(), this.fetchTeachers()]);
    await this.fetchRequests();
  },
  methods: {
    articleTitle,
    formatDate,
    requestTeacherName,
    requestTitle,
    statusClass,
    statusLabel,
    teacherFullName,
    async fetchArticles() {
      try {
        const response = await leaveArticlesApi.list({ active: true });
        this.articles = unwrapList(response);
      } catch {
        this.articles = [];
      }
    },
    async fetchTeachers() {
      try {
        const response = await teachersApi.list({ status: 'activo' });
        this.teachers = unwrapList(response);
      } catch {
        this.teachers = [];
      }
    },
    async fetchRequests() {
      this.loading = true;
      this.error = '';
      try {
        const response = await leaveRequestsApi.list({
          from: this.monthRange.from,
          to: this.monthRange.to,
          user_id: this.filters.user_id,
          leave_article_id: this.filters.leave_article_id,
        });
        this.requests = unwrapList(response);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo cargar el calendario.';
      } finally {
        this.loading = false;
      }
    },
    changeMonth(delta) {
      this.monthDate = new Date(this.monthDate.getFullYear(), this.monthDate.getMonth() + delta, 1);
      this.fetchRequests();
    },
    goToday() {
      this.monthDate = new Date();
      this.fetchRequests();
    },
    eventsByDay(iso) {
      return this.expandedEvents.filter((event) => event.dayIso === iso);
    },
    isToday(iso) {
      return iso === toISO(new Date());
    },
    openDay(iso) {
      this.dayModal.iso = iso;
    },
    openEvent(event) {
      const id = event.id || event.request_id;
      if (id) this.$router.push(`/leave/requests/${id}/review`);
    },
    eventClass(status) {
      return {
        approved: 'bg-green-50 text-green-800 border-green-500',
        pending: 'bg-yellow-50 text-yellow-800 border-yellow-500',
        observed: 'bg-orange-50 text-orange-800 border-orange-500',
        suspended: 'bg-blue-50 text-blue-800 border-blue-500',
        draft: 'bg-gray-50 text-gray-700 border-gray-400',
        rejected: 'bg-red-50 text-red-700 border-red-500 line-through',
        cancelled: 'bg-gray-50 text-gray-500 border-gray-300',
      }[status] || 'bg-gray-50 text-gray-700 border-gray-300';
    },
    eventDotClass(status) {
      return {
        approved: 'bg-green-500',
        pending: 'bg-yellow-500',
        observed: 'bg-orange-500',
        suspended: 'bg-blue-500',
        draft: 'bg-gray-400',
        rejected: 'bg-red-500',
        cancelled: 'bg-gray-300',
      }[status] || 'bg-gray-300';
    },
    shortTeacher(request) {
      const name = requestTeacherName(request);
      const parts = name.split(' ').filter(Boolean);
      if (parts.length >= 2) return `${parts[0][0]}. ${parts[parts.length - 1]}`;
      return name;
    },
  },
};
</script>

<style scoped>
.field {
  @apply w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600;
}
.primary-button {
  @apply inline-flex items-center justify-center px-4 py-2 text-sm font-semibold bg-indigo-700 text-white rounded-md hover:bg-indigo-800 shadow-sm;
}
.secondary-button {
  @apply inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50;
}
.icon-button {
  @apply w-9 h-9 inline-flex items-center justify-center text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50;
}
.module-tabs {
  @apply bg-white border border-gray-200 rounded-lg shadow-sm p-1 mb-5 inline-flex flex-wrap gap-1;
}
.module-tab {
  @apply px-4 py-2 text-sm font-semibold text-gray-600 rounded-md hover:bg-gray-50;
}
.module-tab-active {
  @apply bg-indigo-900 text-white hover:bg-indigo-900;
}

/*
 * @apply bakes fixed light-mode colors into these scoped classes, so the
 * global dark-mode retrofit in style.css (which only overrides classes
 * literally present in the DOM) can never reach them. Same story for a few
 * status/event badge colors below that the retrofit doesn't cover for anyone.
 */
.dark .field {
  @apply bg-gray-700 border-gray-600 text-gray-100;
}
.dark .secondary-button,
.dark .icon-button {
  @apply bg-gray-800 border-gray-600 text-gray-200;
}
.dark .secondary-button:hover,
.dark .icon-button:hover {
  @apply bg-gray-700;
}
.dark .module-tabs {
  @apply bg-gray-800 border-gray-700;
}
.dark .module-tab {
  @apply text-gray-300;
}
.dark .module-tab:hover {
  @apply bg-gray-700;
}

.dark .bg-yellow-100 {
  background-color: rgba(234, 179, 8, 0.2);
}
.dark .bg-orange-100 {
  background-color: rgba(249, 115, 22, 0.2);
}
.dark .bg-blue-100 {
  background-color: rgba(59, 130, 246, 0.2);
}
.dark .text-yellow-800 {
  color: theme('colors.yellow.300');
}
.dark .text-orange-800 {
  color: theme('colors.orange.300');
}
.dark .text-blue-800 {
  color: theme('colors.blue.300');
}
.dark .border-red-200 {
  border-color: rgba(239, 68, 68, 0.35);
}
.dark .border-green-200 {
  border-color: rgba(34, 197, 94, 0.35);
}
.dark .border-yellow-200 {
  border-color: rgba(234, 179, 8, 0.35);
}
.dark .border-orange-200 {
  border-color: rgba(249, 115, 22, 0.35);
}
.dark .border-blue-200 {
  border-color: rgba(59, 130, 246, 0.35);
}
.dark .border-gray-400 {
  border-color: theme('colors.gray.600');
}
.dark .hover\:text-indigo-900:hover {
  color: theme('colors.indigo.200');
}
.dark .hover\:border-indigo-200:hover {
  border-color: rgba(99, 102, 241, 0.5);
}
</style>
